import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import apiConfig from '../config/apiConfig';
import { getRegionalArticleSlug } from '../utils/articleLocaleNavigation';
import fallbackRegions from '../data/regions.json';

const LanguageContext = createContext();

function mergeRegionsWithCatalog(apiRegions = []) {
  const byCode = new Map();
  (fallbackRegions || []).forEach((region) => {
    if (!region?.code) return;
    byCode.set(String(region.code).toUpperCase(), { ...region, isActive: region.isActive !== false });
  });
  (apiRegions || []).forEach((region) => {
    if (!region?.code) return;
    const code = String(region.code).toUpperCase();
    byCode.set(code, {
      ...(byCode.get(code) || {}),
      ...region,
      code,
      isActive: region.isActive !== false,
    });
  });
  return [...byCode.values()].filter((r) => r.isActive !== false);
}

/** Parse /it/article/... → IT. LanguageProvider sits outside <Routes>, so useParams() never works. */
function parseRegionCodeFromPath(pathname) {
  if (!pathname || pathname === '/') return null;
  const match = String(pathname).match(/^\/([a-z]{2})(\/|$)/i);
  return match ? match[1].toUpperCase() : null;
}

function resolveRegionFromPath(pathname, regions) {
  const list = Array.isArray(regions) && regions.length ? regions : mergeRegionsWithCatalog([]);
  const code = parseRegionCodeFromPath(pathname);
  if (code) {
    return (
      list.find((r) => String(r.code).toUpperCase() === code) ||
      list.find((r) => r.code === 'US') ||
      list[0] ||
      null
    );
  }
  return list.find((r) => r.code === 'US') || list[0] || null;
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const catalogRegions = useMemo(() => mergeRegionsWithCatalog([]), []);
  const regionCodeFromPath = useMemo(
    () => parseRegionCodeFromPath(location.pathname),
    [location.pathname]
  );

  // Seed from URL + static catalog immediately so /de/article/... fetches lang=de
  // on the first paint (do not wait for /api/regions — that race soft-404s Googlebot).
  const [availableRegions, setAvailableRegions] = useState(catalogRegions);
  const [region, setRegion] = useState(() =>
    resolveRegionFromPath(
      typeof window !== 'undefined' ? window.location.pathname : '/',
      catalogRegions
    )
  );
  const [language, setLanguage] = useState(() => {
    const initial = resolveRegionFromPath(
      typeof window !== 'undefined' ? window.location.pathname : '/',
      catalogRegions
    );
    return String(initial?.defaultLanguage || 'en').toLowerCase();
  });
  const [loading, setLoading] = useState(true);

  // Fetch available regions on mount
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await axios.get(`${apiConfig.BASE_URL}/regions`);
        if (response.data.success) {
          setAvailableRegions(mergeRegionsWithCatalog(response.data.data || []));
        } else {
          setAvailableRegions(mergeRegionsWithCatalog([]));
        }
      } catch (error) {
        console.error('Error fetching regions:', error);
        setAvailableRegions(mergeRegionsWithCatalog([]));
      } finally {
        setLoading(false);
      }
    };
    fetchRegions();
  }, []);

  // Detect region from URL parameter and set language accordingly
  useEffect(() => {
    const regions =
      availableRegions.length > 0
        ? availableRegions
        : catalogRegions;
    if (regions.length === 0) return;

    const targetRegion = resolveRegionFromPath(
      regionCodeFromPath ? `/${regionCodeFromPath.toLowerCase()}` : location.pathname,
      regions
    );

    if (targetRegion) {
      setRegion(targetRegion);
      const newLang = String(targetRegion.defaultLanguage || 'en').toLowerCase();
      setLanguage(newLang);
      i18n.changeLanguage(newLang);
    }
  }, [regionCodeFromPath, availableRegions, catalogRegions, i18n, location.pathname]);

  const switchLanguage = async (newLang, newRegionCode = null) => {
    const normalizedLang = newLang.toLowerCase();
    let targetRegion;
    
    if (newRegionCode) {
      // Use the specified region (case-insensitive)
      targetRegion = availableRegions.find(
        (r) => String(r.code).toUpperCase() === String(newRegionCode).toUpperCase()
      );
    } else {
      // First, try to find a region where this language is the defaultLanguage
      targetRegion = availableRegions.find(r => 
        r.defaultLanguage && r.defaultLanguage.toLowerCase() === normalizedLang
      );
      
      // If multiple regions have this as default, prefer the first one (or could add logic to prefer current region)
      // If not found, fallback to any region that supports this language
      if (!targetRegion) {
        targetRegion = availableRegions.find(r => 
          r.languages && r.languages.includes(normalizedLang)
        );
      }
    }
    
    if (!targetRegion) {
      console.warn(`Region not found for language: ${newLang}`);
      return;
    }

    setLanguage(normalizedLang);
    setRegion(targetRegion);
    
    // Sync i18next language
    i18n.changeLanguage(normalizedLang);

    // Save preference
    localStorage.setItem('preferredLanguage', normalizedLang);
    localStorage.setItem('preferredRegion', targetRegion.code);

    // Update cookie via API
    try {
      await axios.post(`${apiConfig.BASE_URL}/regions/set`, {
        regionCode: targetRegion.code
      });
    } catch (error) {
      console.warn('Failed to set region cookie:', error);
    }

    // Update URL - preserve current page path
    const currentPath = location.pathname;
    const searchParams = location.search; // Preserve query parameters
    
    // Remove existing region prefix if present
    // Match patterns like: /us, /at/, /at/article/slug, etc.
    let pathWithoutRegion = currentPath;
    
    // Check if path starts with a region code (2 letters, case-insensitive)
    const regionPrefixMatch = currentPath.match(/^\/([a-z]{2})(\/|$)/i);
    if (regionPrefixMatch) {
      // Remove the region prefix (case-insensitive)
      pathWithoutRegion = currentPath.replace(/^\/[a-z]{2}/i, '');
      // Ensure it starts with / if there's more path, or is empty for root
      if (pathWithoutRegion === '') {
        pathWithoutRegion = '/';
      } else if (!pathWithoutRegion.startsWith('/')) {
        pathWithoutRegion = '/' + pathWithoutRegion;
      }
    }
    
    // Build new path with region code prefix
    const regionCode = targetRegion.code.toLowerCase();
    let newPath;

    const articleMatch = pathWithoutRegion.match(/^\/article\/([^/]+)$/);
    const regionalArticleSlug = articleMatch ? getRegionalArticleSlug(targetRegion.code) : null;

    if (articleMatch && regionalArticleSlug) {
      newPath = regionCode === 'us'
        ? `/article/${regionalArticleSlug}`
        : `/${regionCode}/article/${regionalArticleSlug}`;
    } else if (regionCode === 'us') {
      newPath = pathWithoutRegion;
    } else if (pathWithoutRegion === '/') {
      newPath = `/${regionCode}`;
    } else {
      newPath = `/${regionCode}${pathWithoutRegion}`;
    }
    
    // Navigate with preserved query parameters
    navigate(newPath + searchParams);
  };

  const getArticleUrl = (slug) => {
    if (!region) return `/article/${slug}`;
    
    const regionCode = region.code.toLowerCase();
    
    // For US (default English), use no prefix
    if (regionCode === 'us') {
      return `/article/${slug}`;
    }
    return `/${regionCode}/article/${slug}`;
  };

  const getCategoryUrl = (slug) => {
    if (!region) return `/category/${slug}`;
    
    const regionCode = region.code.toLowerCase();
    
    // For US (default English), use no prefix
    if (regionCode === 'us') {
      return `/category/${slug}`;
    }
    return `/${regionCode}/category/${slug}`;
  };

  const getAuthorUrl = (slug) => {
    if (!region) return `/author/${slug}`;
    
    const regionCode = region.code.toLowerCase();
    
    // For US (default English), use no prefix
    if (regionCode === 'us') {
      return `/author/${slug}`;
    }
    return `/${regionCode}/author/${slug}`;
  };

  // Region for API calls: prefer URL path prefix (avoids race when context hasn't loaded)
  const apiRegion = (regionCodeFromPath || region?.code)?.toUpperCase();

  const value = {
    region,
    language,
    availableRegions,
    apiRegion,
    switchLanguage,
    loading,
    getArticleUrl,
    getCategoryUrl,
    getAuthorUrl
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

